import type { ChangeEvent } from 'react';
import { Button, Input, Select } from 'antd';
import { Search } from 'lucide-react';

import {
  ADMIN_RESULTS_SEARCH_MAX_LENGTH,
  type AdminAssessmentFilterStatus,
  type AdminAssessmentFlaggedFilter,
  type AdminAssessmentScoreBand,
} from '@evaltick/shared';

import { ResultsDateRangeField } from './ResultsDateRangeField';
import type { ResultsFilterOption } from './resultsFilterOptions';
import styles from './AssessmentsFilterBar.module.scss';

interface AssessmentsFilterBarProps {
  searchValue: string;
  onSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
  statusOptions: ResultsFilterOption<AdminAssessmentFilterStatus>[];
  status?: AdminAssessmentFilterStatus;
  onStatusChange: (status?: AdminAssessmentFilterStatus) => void;
  scoreBandOptions: ResultsFilterOption<AdminAssessmentScoreBand>[];
  scoreBand?: AdminAssessmentScoreBand;
  onScoreBandChange: (scoreBand?: AdminAssessmentScoreBand) => void;
  flaggedOptions: ResultsFilterOption<AdminAssessmentFlaggedFilter>[];
  flagged?: AdminAssessmentFlaggedFilter;
  onFlaggedChange: (flagged?: AdminAssessmentFlaggedFilter) => void;
  /** `YYYY-MM-DD` bounds — see DATE_PARAM_FORMAT. */
  from?: string;
  to?: string;
  onDateRangeChange: (from?: string, to?: string) => void;
  hasActiveFilters: boolean;
  onClear: () => void;
}

/**
 * The search/filter row above the admin Assessments list. Ordered to match
 * the table's own column order (Status, Score, Flagged, Date) so
 * each filter sits under the column it filters.
 */
export function AssessmentsFilterBar({
  searchValue,
  onSearchChange,
  statusOptions,
  status,
  onStatusChange,
  scoreBandOptions,
  scoreBand,
  onScoreBandChange,
  flaggedOptions,
  flagged,
  onFlaggedChange,
  from,
  to,
  onDateRangeChange,
  hasActiveFilters,
  onClear,
}: AssessmentsFilterBarProps) {
  return (
    <div className={styles.filterBar} role="search" aria-label="Filter assessments">
      <Input
        placeholder="Search name, email, tag or package"
        aria-label="Search assessments"
        value={searchValue}
        onChange={onSearchChange}
        maxLength={ADMIN_RESULTS_SEARCH_MAX_LENGTH}
        allowClear
        prefix={<Search className={styles.inputIcon} />}
        className={styles.search}
      />
      <Select<AdminAssessmentFilterStatus>
        placeholder="Status"
        aria-label="Filter assessments by status"
        value={status}
        onChange={(value) => onStatusChange(value || undefined)}
        options={statusOptions}
        allowClear
        className={styles.status}
      />
      <Select<AdminAssessmentScoreBand>
        placeholder="Score"
        aria-label="Filter assessments by score"
        value={scoreBand}
        onChange={(value) => onScoreBandChange(value || undefined)}
        options={scoreBandOptions}
        allowClear
        className={styles.score}
      />
      <Select<AdminAssessmentFlaggedFilter>
        placeholder="Flagged"
        aria-label="Filter assessments by flagged"
        value={flagged}
        onChange={(value) => onFlaggedChange(value || undefined)}
        options={flaggedOptions}
        allowClear
        className={styles.flagged}
      />
      <ResultsDateRangeField
        ariaLabel="Filter assessments by date"
        from={from}
        to={to}
        onChange={onDateRangeChange}
        className={styles.dates}
      />
      <Button type="link" onClick={onClear} disabled={!hasActiveFilters} className={styles.clear}>
        Clear filters
      </Button>
    </div>
  );
}
